import { cn } from "../../lib/format";

/**
 * ilmli wordmark = the ear+scribble mark followed by the italic wordmark.
 *
 * Variants:
 *   - "ink" (default, two-tone): ink outline + terracotta inner. The
 *     primary brand. Use on paper / light backgrounds.
 *   - "paper": paper outline + paper inner. For dark backgrounds.
 *   - "terracotta": terracotta outline + paper inner. For terracotta
 *     surfaces, accents.
 *
 * The mark is an inline SVG drawn at 0.85em of the wordmark height so it
 * sits comfortably next to the descenders of `g`-style italics.
 */
export default function Logo({
  size = "md",
  variant = "ink",
}: {
  size?: "sm" | "md" | "lg" | "xl";
  variant?: "ink" | "paper" | "terracotta";
}) {
  const sizes = {
    sm: "text-[1.5rem]",
    md: "text-[1.9rem]",
    lg: "text-[2.75rem]",
    xl: "text-[clamp(4rem,9vw,8rem)]",
  };
  const wordColor = {
    ink: "text-ink",
    paper: "text-paper",
    terracotta: "text-terracotta",
  }[variant];
  const outlineColor = {
    ink: "#17171A",
    paper: "#F4ECDD",
    terracotta: "#B8472C",
  }[variant];
  const innerColor = {
    ink: "#B8472C",
    paper: "#F4ECDD",
    terracotta: "#F4ECDD",
  }[variant];
  return (
    <span
      className={cn(
        "font-serif leading-none inline-flex items-baseline",
        sizes[size],
        wordColor,
      )}
    >
      <span
        className="inline-flex items-end"
        style={{ height: "0.85em", marginRight: "0.18em" }}
        aria-hidden
      >
        <Mark outline={outlineColor} inner={innerColor} />
      </span>
      <span className="italic font-medium tracking-tight">ilmli</span>
    </span>
  );
}

function Mark({ outline, inner }: { outline: string; inner: string }) {
  return (
    <svg
      viewBox="0 0 70 90"
      xmlns="http://www.w3.org/2000/svg"
      style={{ height: "100%", width: "auto" }}
    >
      <path
        d="M 12 26 C 12 12, 26 6, 42 8 C 58 10, 64 22, 60 38 C 56 54, 50 60, 46 70 C 44 78, 38 84, 30 84 C 22 84, 18 76, 18 68 C 18 54, 12 40, 12 26 Z"
        stroke={outline}
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <g transform="translate(20 20) scale(1.5) translate(-2 -3)">
        <path
          d="M3,13.5793719 C9.98914658,5.83454059 14.2442185,2.78592627 15.7652158,4.43352892 C18.0467117,6.90493289 7.55581053,16.1455344 9.47834357,17.954063 C11.4008766,19.7625917 16.9959382,11.5719148 19.0578414,12.4109285 C21.1197445,13.2499421 16.1152903,18.1722847 17.4055985,18.9997829 C18.2658039,19.5514483 19.3191667,19.0606734 20.5656867,17.527458"
          stroke={inner}
          strokeWidth="2.67"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </g>
    </svg>
  );
}
